import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Heart, MessageCircle, Bookmark, User as UserIcon, Send, Gift, ChevronDown, ChevronUp, Play, Music, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Media type detection helper (same as CreatePost)
const detectMediaType = (url) => {
  if (!url) return { type: 'unknown', embedUrl: null, originalUrl: url };
  
  const urlLower = url.toLowerCase();
  
  // YouTube
  if (urlLower.includes('youtube.com/watch') || urlLower.includes('youtu.be/')) {
    const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1];
    return { type: 'youtube', embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : null, videoId, originalUrl: url };
  }
  
  // YouTube Music
  if (urlLower.includes('music.youtube.com')) {
    const videoId = url.match(/[?&]v=([^&]+)/)?.[1];
    return { type: 'youtube_music', embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : null, videoId, originalUrl: url };
  }
  
  // TikTok
  if (urlLower.includes('tiktok.com')) {
    return { type: 'tiktok', originalUrl: url };
  }
  
  // SoundCloud
  if (urlLower.includes('soundcloud.com')) {
    return { type: 'soundcloud', embedUrl: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`, originalUrl: url };
  }
  
  // Spotify
  if (urlLower.includes('spotify.com')) {
    const match = url.match(/spotify\.com\/(track|album|playlist|episode)\/([^?]+)/);
    if (match) {
      return { type: 'spotify', embedUrl: `https://open.spotify.com/embed/${match[1]}/${match[2]}`, contentType: match[1], contentId: match[2], originalUrl: url };
    }
  }
  
  // Facebook Video or Post
  if (urlLower.includes('facebook.com')) {
    const isVideo = urlLower.includes('/videos/') || urlLower.includes('/watch') || urlLower.includes('/reel');
    return { type: isVideo ? 'facebook_video' : 'facebook', originalUrl: url };
  }
  
  // Instagram
  if (urlLower.includes('instagram.com')) {
    return { type: 'instagram', originalUrl: url };
  }
  
  // Direct video URLs
  if (urlLower.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i)) {
    return { type: 'video', embedUrl: url, originalUrl: url };
  }
  
  // Direct image URLs (or default)
  return { type: 'image', embedUrl: url, originalUrl: url };
};

// Media Embed Component for Feed using react-social-media-embed with auto-pause on scroll
const MediaEmbed = ({ mediaUrl, contentType, postId }) => {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  
  const mediaInfo = detectMediaType(mediaUrl);
  const type = contentType !== 'image' ? contentType : mediaInfo.type;
  const embedUrl = mediaInfo.embedUrl;

  // Intersection Observer to detect when media goes out of view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
          
          // Auto-pause when scrolled out of view
          if (!entry.isIntersecting) {
            // Pause HTML5 video
            if (videoRef.current) {
              videoRef.current.pause();
            }
            
            // For YouTube iframes, we can pause by manipulating the src or using postMessage
            if (iframeRef.current && (type === 'youtube' || type === 'youtube_music')) {
              try {
                iframeRef.current.contentWindow.postMessage(
                  JSON.stringify({ event: 'command', func: 'pauseVideo' }), 
                  '*'
                );
              } catch (e) {
                // Fallback: reload iframe to stop video
                // This is less elegant but works across all browsers
              }
            }
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of the element is visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [type]);
  
  switch (type) {
    case 'youtube':
    case 'youtube_music':
      // Add enablejsapi=1 to allow postMessage control
      const youtubeEmbedUrl = embedUrl ? `${embedUrl}?enablejsapi=1` : embedUrl;
      return (
        <div ref={containerRef} className="aspect-video bg-black">
          <iframe
            ref={iframeRef}
            src={youtubeEmbedUrl}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="YouTube video"
          />
        </div>
      );
    
    case 'tiktok':
      // TikTok requires oEmbed API or just link to the video
      const tiktokVideoId = mediaUrl.match(/video\/(\d+)/)?.[1];
      return (
        <div ref={containerRef} className="flex flex-col items-center justify-center py-4 bg-gray-900 min-h-[400px]">
          <div className="text-center p-6">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Play className="h-8 w-8 text-pink-500" />
            </div>
            <p className="text-white font-medium mb-2">TikTok Video</p>
            <p className="text-gray-400 text-sm mb-4">Click to watch on TikTok</p>
            <a 
              href={mediaUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-full transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Watch on TikTok
            </a>
          </div>
        </div>
      );
    
    case 'soundcloud':
      return (
        <div ref={containerRef} className="px-4 py-2">
          <iframe
            ref={iframeRef}
            src={embedUrl}
            className="w-full rounded-xl"
            height="166"
            allow="autoplay"
            title="SoundCloud"
          />
        </div>
      );
    
    case 'spotify':
      return (
        <div ref={containerRef} className="px-4 py-2">
          <iframe
            ref={iframeRef}
            src={embedUrl}
            className="w-full rounded-xl"
            height="352"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            title="Spotify"
          />
        </div>
      );
    
    case 'facebook':
    case 'facebook_video':
      return (
        <div ref={containerRef} className="flex flex-col items-center justify-center py-4 bg-blue-900/30 min-h-[300px]">
          <div className="text-center p-6">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Play className="h-8 w-8 text-blue-500" />
            </div>
            <p className="text-white font-medium mb-2">Facebook Content</p>
            <p className="text-gray-400 text-sm mb-4">Click to view on Facebook</p>
            <a 
              href={mediaUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              View on Facebook
            </a>
          </div>
        </div>
      );
    
    case 'instagram':
      return (
        <div ref={containerRef} className="flex flex-col items-center justify-center py-4 bg-gradient-to-br from-purple-900/30 to-pink-900/30 min-h-[300px]">
          <div className="text-center p-6">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Play className="h-8 w-8 text-white" />
            </div>
            <p className="text-white font-medium mb-2">Instagram Content</p>
            <p className="text-gray-400 text-sm mb-4">Click to view on Instagram</p>
            <a 
              href={mediaUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-full transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              View on Instagram
            </a>
          </div>
        </div>
      );
    
    case 'video':
      return (
        <div ref={containerRef} className="bg-black">
          <video
            ref={videoRef}
            src={mediaUrl}
            controls
            className="w-full max-h-[600px]"
          />
        </div>
      );
    
    case 'image':
    default:
      return (
        <img 
          src={mediaUrl} 
          alt="Post content" 
          className="w-full max-h-[600px] object-cover"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/400x300?text=Media+Not+Found';
          }}
        />
      );
  }
};

// Get media type icon
const getMediaTypeIcon = (contentType, mediaUrl) => {
  const type = contentType !== 'image' ? contentType : detectMediaType(mediaUrl).type;
  switch (type) {
    case 'youtube':
    case 'youtube_music':
    case 'tiktok':
    case 'facebook_video':
    case 'video':
      return <Play className="h-3 w-3" />;
    case 'soundcloud':
    case 'spotify':
      return <Music className="h-3 w-3" />;
    default:
      return <ImageIcon className="h-3 w-3" />;
  }
};

// Get media type label
const getMediaTypeLabel = (contentType, mediaUrl) => {
  const type = contentType !== 'image' ? contentType : detectMediaType(mediaUrl).type;
  const labels = {
    youtube: 'YouTube',
    youtube_music: 'YouTube Music',
    tiktok: 'TikTok',
    soundcloud: 'SoundCloud',
    spotify: 'Spotify',
    facebook: 'Facebook',
    facebook_video: 'Facebook Video',
    instagram: 'Instagram',
    video: 'Video',
    image: 'Image'
  };
  return labels[type] || 'Media';
};

function GlobalFeed() {
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [postComments, setPostComments] = useState({});
  const [submittingComment, setSubmittingComment] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
    fetchInitialPosts();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch {}
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const userMap = {};
        data.forEach(u => { userMap[u.user_id] = u; });
        setUsers(userMap);
      }
    } catch {}
  };

  const fetchInitialPosts = async (isRefresh = false, retryCount = 0) => {
    try {
      const response = await fetch(`${API}/posts?skip=0&limit=20`, { credentials: 'include' });
      if (!response.ok) {
        if (retryCount < 2) {
          await new Promise(r => setTimeout(r, 1000));
          return fetchInitialPosts(isRefresh, retryCount + 1);
        }
        throw new Error('Server error');
      }
      const data = await response.json();
      setPosts(data);
      setSkip(20);
      setHasMore(data.length === 20);
    } catch (error) {
      if (retryCount < 2) {
        await new Promise(r => setTimeout(r, 1000));
        return fetchInitialPosts(isRefresh, retryCount + 1);
      }
      if (!isRefresh) {
        toast.error('Failed to load posts');
      }
    } finally {
      if (!isRefresh) {
        setIsLoading(false);
      }
    }
  };

  const loadMorePosts = async () => {
    try {
      const response = await fetch(`${API}/posts?skip=${skip}&limit=20`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      if (data.length === 0) {
        setHasMore(false);
      } else {
        setPosts(prev => [...prev, ...data]);
        setSkip(prev => prev + 20);
        setHasMore(data.length === 20);
      }
    } catch {
      toast.error('Failed to load more posts');
    }
  };

  const handleLike = async (postId) => {
    if (!currentUser) {
      toast.error('Please login to like posts');
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API}/posts/${postId}/like`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (!response.ok) throw new Error(result.detail || 'Failed to like post');
      
      // Update local state
      setPosts(prev => prev.map(post => {
        if (post.post_id === postId) {
          const isLiking = result.message === 'Post liked';
          return {
            ...post,
            likes_count: post.likes_count + (isLiking ? 1 : -1)
          };
        }
        return post;
      }));
      
      // Show vote reward notification if earned
      if (result.votes_earned > 0) {
        toast.success(
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-secondary" />
            <span>Post liked! You earned <strong>+{result.votes_earned} vote</strong></span>
          </div>
        );
        // Update current user's vote count
        setCurrentUser(prev => ({
          ...prev,
          free_votes_remaining: (prev?.free_votes_remaining || 0) + result.votes_earned
        }));
      } else if (result.message === 'Post liked' && result.is_own_post) {
        toast.info('Post liked! (No votes for liking your own post)');
      } else if (result.message === 'Post liked') {
        toast.success('Post liked!');
      } else {
        toast.info('Post unliked');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggleComments = async (postId) => {
    const isExpanded = expandedComments[postId];
    
    if (!isExpanded && !postComments[postId]) {
      // Fetch comments
      try {
        const response = await fetch(`${API}/posts/${postId}/comments`, { credentials: 'include' });
        if (response.ok) {
          const comments = await response.json();
          setPostComments(prev => ({ ...prev, [postId]: comments }));
        }
      } catch (error) {
        console.error('Failed to fetch comments');
      }
    }
    
    setExpandedComments(prev => ({ ...prev, [postId]: !isExpanded }));
  };

  const handleComment = async (postId) => {
    if (!currentUser) {
      toast.error('Please login to comment');
      navigate('/login');
      return;
    }

    const content = commentInputs[postId]?.trim();
    if (!content) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmittingComment(postId);

    try {
      const response = await fetch(`${API}/posts/${postId}/comments?content=${encodeURIComponent(content)}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (!response.ok) throw new Error(result.detail || 'Failed to add comment');
      
      // Update local state
      setPosts(prev => prev.map(post => {
        if (post.post_id === postId) {
          return { ...post, comments_count: (post.comments_count || 0) + 1 };
        }
        return post;
      }));
      
      // Add comment to local state
      setPostComments(prev => ({
        ...prev,
        [postId]: [result, ...(prev[postId] || [])]
      }));
      
      // Clear input
      setCommentInputs(prev => ({ ...prev, [postId]: '' }));
      
      // Show vote reward notification if earned
      if (result.votes_earned > 0) {
        toast.success(
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-secondary" />
            <span>Comment added! You earned <strong>+{result.votes_earned} votes</strong></span>
          </div>
        );
        // Update current user's vote count
        setCurrentUser(prev => ({
          ...prev,
          free_votes_remaining: (prev?.free_votes_remaining || 0) + result.votes_earned
        }));
      } else {
        toast.success('Comment added!');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmittingComment(null);
    }
  };

  const getUserName = (userId, post) => {
    if (post?.author_name) return post.author_name;
    const user = users[userId];
    return user ? (user.stage_name || user.name || 'Unknown') : 'Unknown';
  };

  const getUserPicture = (userId, post) => {
    if (post?.author_picture) return post.author_picture;
    const user = users[userId];
    return user?.picture;
  };

  // Admin pin post function
  const handlePinPost = async (postId) => {
    if (!currentUser?.is_admin) return;
    
    try {
      const response = await fetch(`${API}/admin/posts/${postId}/pin`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Post updated successfully');
        // Refresh posts to show pinned post at top
        await fetchInitialPosts(true);
      } else {
        toast.error(data.detail || 'Failed to pin post');
      }
    } catch (error) {
      console.error('Pin post error:', error);
      toast.error('Failed to pin post');
    }
  };

  if (isLoading && posts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {posts.length === 0 ? (
            <Card className="glass-panel p-12 text-center">
              <p className="text-xl text-muted-foreground">No posts yet. Be the first to share!</p>
            </Card>
          ) : (
            posts.map((post) => (
              <Card key={post.post_id} className={`glass-panel overflow-hidden ${post.is_pinned ? 'border-2 border-yellow-500' : ''}`} data-testid={`post-${post.post_id}`}>
                {/* Pinned Badge */}
                {post.is_pinned && (
                  <div className="bg-yellow-500 text-black px-4 py-1 text-sm font-bold flex items-center gap-2">
                    <span>📌</span> Pinned Post
                  </div>
                )}
                
                <div className="p-4">
                  <div 
                    className="flex items-center gap-3 mb-4 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => navigate(`/profile/${post.user_id}`)}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">
                      {getUserPicture(post.user_id, post) ? (
                        <img src={getUserPicture(post.user_id, post)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <UserIcon className="h-5 w-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold">{getUserName(post.user_id, post)}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(post.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {currentUser && post.user_id === currentUser.user_id && (
                      <span className="ml-auto text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                        Your post
                      </span>
                    )}
                    {/* Admin Pin Button */}
                    {currentUser?.is_admin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePinPost(post.post_id);
                        }}
                        className={`ml-auto ${post.is_pinned ? 'text-yellow-500' : 'text-muted-foreground'}`}
                        data-testid={`pin-post-${post.post_id}`}
                      >
                        📌 {post.is_pinned ? 'Unpin' : 'Pin'}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Media Content */}
                {post.media_url && (
                  <div className="relative">
                    {/* Media Type Badge */}
                    <div className="absolute top-2 right-2 z-10">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-full">
                        {getMediaTypeIcon(post.content_type, post.media_url)}
                        {getMediaTypeLabel(post.content_type, post.media_url)}
                      </span>
                    </div>
                    <MediaEmbed mediaUrl={post.media_url} contentType={post.content_type} postId={post.post_id} />
                  </div>
                )}

                <div className="p-4">
                  {post.caption && (
                    <p className="mb-4">{post.caption}</p>
                  )}

                  <div className="flex items-center gap-6">
                    <button
                      data-testid={`like-btn-${post.post_id}`}
                      onClick={() => handleLike(post.post_id)}
                      className="flex items-center gap-2 hover:text-accent transition-colors group"
                    >
                      <Heart className="h-5 w-5 group-hover:scale-110 transition-transform" />
                      <span>{post.likes_count || 0}</span>
                      {currentUser && post.user_id !== currentUser.user_id && (
                        <span className="text-xs text-secondary opacity-0 group-hover:opacity-100 transition-opacity">+1</span>
                      )}
                    </button>
                    <button 
                      className="flex items-center gap-2 hover:text-primary transition-colors group"
                      onClick={() => toggleComments(post.post_id)}
                      data-testid={`comment-toggle-${post.post_id}`}
                    >
                      <MessageCircle className="h-5 w-5 group-hover:scale-110 transition-transform" />
                      <span>{post.comments_count || 0}</span>
                      {currentUser && post.user_id !== currentUser.user_id && (
                        <span className="text-xs text-secondary opacity-0 group-hover:opacity-100 transition-opacity">+2</span>
                      )}
                      {expandedComments[post.post_id] ? (
                        <ChevronUp className="h-4 w-4 ml-1" />
                      ) : (
                        <ChevronDown className="h-4 w-4 ml-1" />
                      )}
                    </button>
                    <button className="flex items-center gap-2 hover:text-secondary transition-colors ml-auto">
                      <Bookmark className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Comments Section */}
                  {expandedComments[post.post_id] && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      {/* Comment Input */}
                      <div className="flex gap-2 mb-4">
                        <Input
                          placeholder="Write a comment..."
                          value={commentInputs[post.post_id] || ''}
                          onChange={(e) => setCommentInputs(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                          onKeyPress={(e) => e.key === 'Enter' && handleComment(post.post_id)}
                          className="flex-1 bg-white/5 border-white/10 h-10 rounded-full"
                          data-testid={`comment-input-${post.post_id}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleComment(post.post_id)}
                          disabled={submittingComment === post.post_id || !commentInputs[post.post_id]?.trim()}
                          className="rounded-full h-10 px-4 bg-primary"
                          data-testid={`submit-comment-${post.post_id}`}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Comments List */}
                      <div className="space-y-3 max-h-[300px] overflow-y-auto">
                        {(postComments[post.post_id] || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-2">
                            No comments yet. Be the first!
                          </p>
                        ) : (
                          (postComments[post.post_id] || []).map((comment) => (
                            <div key={comment.comment_id} className="flex gap-3 p-2 rounded-lg bg-white/5">
                              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {getUserPicture(comment.user_id) ? (
                                  <img src={getUserPicture(comment.user_id)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <UserIcon className="h-4 w-4" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm">
                                  <span className="font-medium">{getUserName(comment.user_id)}</span>
                                  <span className="text-muted-foreground ml-2 text-xs">
                                    {new Date(comment.created_at).toLocaleDateString()}
                                  </span>
                                </p>
                                <p className="text-sm text-muted-foreground">{comment.content}</p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}

          {hasMore && (
            <div className="text-center py-8">
              <Button
                onClick={loadMorePosts}
                className="bg-primary rounded-full"
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GlobalFeed;
